import { useCallback, useEffect, useRef, useState } from "react";
import api from "../api";

/**
 * Status panel for the verification watcher.
 *
 * The watcher runs as its own systemd timer, not inside Flask, so the only way
 * to know it is alive is what it writes to disk. Without this panel "no emails
 * today" is indistinguishable from "the job died three weeks ago".
 *
 * Two independent signals, deliberately kept separate:
 *   - heartbeat: a 30s timer writing a timestamp. Proves the host and systemd
 *     are alive right now. Says nothing about AGOL or email.
 *   - runs: the watcher itself, every 15 min on weekdays. Proves the whole
 *     path works, but is silent for 15 minutes at a time.
 */

type HistoryEntry = {
    at: string;
    ok: boolean;
    found: number;
    emailed: number;
    error?: string;
};

type DayBucket = { runs: number; failed: number; emailed: number };

type Heartbeat = {
    at?: string;
    boot?: string;
    age_seconds?: number;
    alive?: boolean;
    error?: string;
};

type WatcherStatus = {
    status: string;
    configured: boolean;
    health: "healthy" | "idle" | "stale" | "failing" | "unknown";
    in_window?: boolean;
    age_minutes?: number | null;
    last_run_at?: string | null;
    last_run_ok?: boolean | null;
    last_error?: string | null;
    last_email_at?: string | null;
    last_email_count?: number | null;
    watermark?: number | null;
    totals?: { runs?: number; emails?: number; records?: number };
    recipients?: { to?: string[]; cc?: string[] };
    schedule?: string;
    history?: HistoryEntry[];
    heartbeat?: Heartbeat | null;
    daily?: Record<string, DayBucket>;
    message?: string;
};

const HEALTH: Record<string, { label: string; dot: string; text: string; blurb: string }> = {
    healthy: { label: "Active", dot: "bg-green-500", text: "text-green-700", blurb: "Running on schedule." },
    idle: { label: "Idle", dot: "bg-slate-400", text: "text-slate-600", blurb: "Outside the 8am–4pm window. This is expected." },
    stale: { label: "Stale", dot: "bg-amber-500", text: "text-amber-700", blurb: "Should have run by now but hasn't. Check the timer on the server." },
    failing: { label: "Failing", dot: "bg-red-500", text: "text-red-700", blurb: "The last run errored. New verification records are not being emailed." },
    unknown: { label: "Unknown", dot: "bg-slate-300", text: "text-slate-500", blurb: "No run history yet." },
};

const POLL_MS = 10000;      // heartbeat is 30s; poll faster so the log feels live
const MAX_LINES = 300;
const UPTIME_DAYS = 90;

function timeAgo(iso?: string | null): string {
    if (!iso) return "never";
    const mins = Math.floor((Date.now() - new Date(iso).getTime()) / 60000);
    if (mins < 1) return "just now";
    if (mins < 60) return `${mins} min ago`;
    const hrs = Math.floor(mins / 60);
    if (hrs < 24) return `${hrs} hr ago`;
    return `${Math.floor(hrs / 24)} d ago`;
}

function localTime(iso?: string | null): string {
    if (!iso) return "—";
    return new Date(iso).toLocaleString(undefined, {
        month: "numeric", day: "numeric", hour: "numeric", minute: "2-digit",
    });
}

/** Clock time for today's events, date included for anything older.
 *
 * The feed is in arrival order, and the first poll prints the last check
 * whenever it happened. On a Sunday that check is Friday's, and printed as a
 * bare 15:46:08 among live heartbeats it read as though the watcher had just
 * run -- the one thing the panel exists to tell you.
 */
function clockTime(d: Date): string {
    const today = new Date();
    const sameDay = d.getFullYear() === today.getFullYear()
        && d.getMonth() === today.getMonth()
        && d.getDate() === today.getDate();
    const clock = d.toLocaleTimeString(undefined, { hour12: false });
    if (sameDay) return clock;
    return `${d.toLocaleDateString(undefined, { month: "numeric", day: "numeric" })} ${clock}`;
}

/** Build a contiguous day-by-day series ending today, filling gaps with nulls. */
function buildUptimeSeries(daily: Record<string, DayBucket>) {
    const out: { date: string; bucket: DayBucket | null }[] = [];
    const today = new Date();
    for (let i = UPTIME_DAYS - 1; i >= 0; i--) {
        const d = new Date(today);
        d.setDate(d.getDate() - i);
        // Local date key, matching how the watcher writes it.
        const key = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
        out.push({ date: key, bucket: daily[key] ?? null });
    }
    return out;
}

type LogLine = { key: string; at: Date; kind: "beat" | "run" | "mail" | "warn"; text: string };

export default function VerificationWatcherCard() {
    const [data, setData] = useState<WatcherStatus | null>(null);
    const [error, setError] = useState<string | null>(null);
    const [loading, setLoading] = useState(true);
    const [lines, setLines] = useState<LogLine[]>([]);

    // Track what we've already logged so a poll doesn't re-print the same event.
    const seenBeat = useRef<string | null>(null);
    const seenRun = useRef<string | null>(null);
    const logRef = useRef<HTMLDivElement | null>(null);
    const autoScroll = useRef(true);

    const push = useCallback((kind: LogLine["kind"], text: string, at?: Date) => {
        const stamp = at ?? new Date();
        setLines((prev) => {
            const next = [...prev, { key: `${kind}-${stamp.getTime()}-${Math.random()}`, at: stamp, kind, text }];
            return next.slice(-MAX_LINES);
        });
    }, []);

    const load = useCallback(async () => {
        try {
            const res = await api.get("/watcher-status");
            const d: WatcherStatus = res.data;
            setData(d);
            setError(null);

            const hb = d.heartbeat;
            if (hb?.at && hb.at !== seenBeat.current) {
                seenBeat.current = hb.at;
                const age = hb.age_seconds ?? 0;
                push("beat", `heartbeat ok        (age ${age.toFixed(0)}s)`, new Date(hb.at));
            } else if (hb && hb.alive === false) {
                push("warn", `heartbeat STALE     (age ${(hb.age_seconds ?? 0).toFixed(0)}s) — host or timer down`);
            } else if (hb?.error) {
                push("warn", `heartbeat unreadable: ${hb.error}`);
            }

            if (d.last_run_at && d.last_run_at !== seenRun.current) {
                seenRun.current = d.last_run_at;
                const at = new Date(d.last_run_at);
                if (d.last_run_ok === false) {
                    push("warn", `check FAILED — ${d.last_error ?? "unknown error"}`, at);
                } else {
                    const last = (d.history ?? []).slice(-1)[0];
                    const found = last?.found ?? 0;
                    if (found > 0) {
                        push("mail", `check ok — ${found} new record(s), alert sent`, at);
                    } else {
                        push("run", `check ok — no new records`, at);
                    }
                }
            }
        } catch (err: any) {
            setError(err?.response?.data?.message || err.message);
        } finally {
            setLoading(false);
        }
    }, [push]);

    useEffect(() => {
        load();
        const t = setInterval(load, POLL_MS);
        return () => clearInterval(t);
    }, [load]);

    // Keep the log pinned to the bottom unless the user has scrolled up.
    useEffect(() => {
        const el = logRef.current;
        if (el && autoScroll.current) el.scrollTop = el.scrollHeight;
    }, [lines]);

    if (loading) return <div className="text-sm text-slate-500">Loading watcher status…</div>;
    if (error) return <div className="text-sm text-red-600">Could not load status: {error}</div>;
    if (!data) return null;

    const hb = data.heartbeat;
    const beatAlive = hb?.alive === true;

    if (!data.configured) {
        return (
            <div className="space-y-3 text-sm text-slate-600">
                <p className="font-medium">Watcher has not run on this server yet.</p>
                <p>{data.message}</p>
                {hb && (
                    <p className={beatAlive ? "text-green-700" : "text-amber-700"}>
                        Host heartbeat: {beatAlive ? "alive" : "not beating"}
                        {hb.at ? ` (last ${timeAgo(hb.at)})` : ""}
                    </p>
                )}
            </div>
        );
    }

    const h = HEALTH[data.health] || HEALTH.unknown;
    const totals = data.totals || {};
    const daily = data.daily || {};
    const series = buildUptimeSeries(daily);

    const totalRuns = series.reduce((n, d) => n + (d.bucket?.runs ?? 0), 0);
    const totalFailed = series.reduce((n, d) => n + (d.bucket?.failed ?? 0), 0);
    const uptimePct = totalRuns > 0 ? ((totalRuns - totalFailed) / totalRuns) * 100 : null;
    const activeDays = series.filter((d) => d.bucket).length;

    const barColour = (b: DayBucket | null) => {
        if (!b || b.runs === 0) return "bg-slate-200";                 // no runs: weekend, or before install
        if (b.failed === 0) return "bg-green-500";
        if (b.failed >= b.runs) return "bg-red-500";
        return "bg-amber-400";
    };

    const lineColour = (k: LogLine["kind"]) =>
        k === "warn" ? "text-red-400" : k === "mail" ? "text-green-400" : k === "run" ? "text-sky-300" : "text-slate-400";

    return (
        <div className="space-y-5 text-sm">
            {/* ---- Uptime bar ---- */}
            <div className="rounded-lg border border-slate-200 p-3">
                <div className="flex items-baseline justify-between mb-2">
                    <span className="text-xs text-slate-500">
                        Watcher checks over the past {UPTIME_DAYS} days
                    </span>
                    <span className={`text-xs font-semibold ${h.text}`}>{h.label}</span>
                </div>

                <div className="flex items-end gap-[2px] h-10">
                    {series.map((d) => (
                        <div
                            key={d.date}
                            title={
                                d.bucket
                                    ? `${d.date}: ${d.bucket.runs} checks, ${d.bucket.failed} failed, ${d.bucket.emailed} record(s) alerted`
                                    : `${d.date}: no checks (weekend or before install)`
                            }
                            className={`flex-1 min-w-[3px] h-full rounded-sm ${barColour(d.bucket)}`}
                        />
                    ))}
                </div>

                <div className="flex items-center justify-between mt-1 text-[11px] text-slate-400">
                    <span>{UPTIME_DAYS} days ago</span>
                    <span className="text-slate-600">
                        {uptimePct === null
                            ? "no data yet"
                            : `${uptimePct.toFixed(1)} % of ${totalRuns} checks succeeded`}
                        <span className="text-slate-400"> · {activeDays} active day{activeDays === 1 ? "" : "s"}</span>
                    </span>
                    <span>Today</span>
                </div>
            </div>

            {/* ---- Live log ---- */}
            <div>
                <div className="flex items-center justify-between mb-1">
                    <span className="text-xs text-slate-500">Live activity</span>
                    <span className="flex items-center gap-1.5 text-xs">
                        <span
                            className={`inline-block h-2 w-2 rounded-full ${beatAlive ? "bg-green-500 animate-pulse" : "bg-red-500"}`}
                        />
                        <span className={beatAlive ? "text-green-700" : "text-red-700"}>
                            {beatAlive ? "heartbeat live" : "no heartbeat"}
                        </span>
                    </span>
                </div>

                <div
                    ref={logRef}
                    onScroll={(e) => {
                        const el = e.currentTarget;
                        autoScroll.current = el.scrollHeight - el.scrollTop - el.clientHeight < 24;
                    }}
                    className="h-40 overflow-y-auto rounded-lg bg-slate-900 px-3 py-2 font-mono text-[11px] leading-relaxed"
                >
                    {lines.length === 0 ? (
                        <div className="text-slate-500">waiting for the next heartbeat…</div>
                    ) : (
                        lines.map((l) => (
                            <div key={l.key} className={lineColour(l.kind)}>
                                <span className="text-slate-600">{clockTime(l.at)}</span>{" "}
                                {l.kind === "beat" ? "·" : l.kind === "warn" ? "!" : "▸"} {l.text}
                            </div>
                        ))
                    )}
                </div>

                <div className="mt-1 text-[11px] text-slate-400">
                    Heartbeat every 30s proves the server and its timers are alive. The
                    watcher itself checks every 15 min on weekdays — that's the line
                    that proves AGOL and email are working.
                </div>
            </div>

            {/* ---- Detail ---- */}
            <div className="flex items-center gap-2">
                <span className={`inline-block h-2.5 w-2.5 rounded-full ${h.dot}`} />
                <span className={`font-semibold ${h.text}`}>{h.label}</span>
                <span className="text-slate-500">— {h.blurb}</span>
            </div>

            {data.last_error && (
                <div className="rounded bg-red-50 border border-red-200 p-2 text-red-800">
                    <div className="font-medium">Last error</div>
                    <div className="font-mono text-xs break-all">{data.last_error}</div>
                </div>
            )}

            <dl className="grid grid-cols-2 gap-x-6 gap-y-2">
                <div>
                    <dt className="text-slate-500">Last checked</dt>
                    <dd className="font-medium">
                        {timeAgo(data.last_run_at)}
                        <span className="text-slate-400 font-normal"> · {localTime(data.last_run_at)}</span>
                    </dd>
                </div>
                <div>
                    <dt className="text-slate-500">Last email sent</dt>
                    <dd className="font-medium">
                        {data.last_email_at ? (
                            <>
                                {timeAgo(data.last_email_at)}
                                <span className="text-slate-400 font-normal">
                                    {" "}· {data.last_email_count} record{data.last_email_count === 1 ? "" : "s"}
                                </span>
                            </>
                        ) : "none yet"}
                    </dd>
                </div>
                <div>
                    <dt className="text-slate-500">Schedule</dt>
                    <dd className="font-medium">{data.schedule}</dd>
                </div>
                <div>
                    <dt className="text-slate-500">Server up since</dt>
                    <dd className="font-medium">{hb?.boot ?? "—"}</dd>
                </div>
                <div>
                    <dt className="text-slate-500">Checks / emails</dt>
                    <dd className="font-medium">
                        {totals.runs ?? 0} / {totals.emails ?? 0}
                        <span className="text-slate-400 font-normal"> ({totals.records ?? 0} records)</span>
                    </dd>
                </div>
                <div>
                    <dt className="text-slate-500">Newest record reported</dt>
                    <dd className="font-medium">#{data.watermark}</dd>
                </div>
            </dl>

            {data.recipients?.to?.length ? (
                <div>
                    <div className="text-slate-500">Alerts go to</div>
                    <div className="font-medium">{data.recipients.to.join(", ")}</div>
                    {data.recipients.cc?.length ? (
                        <div className="text-slate-500 text-xs">cc {data.recipients.cc.join(", ")}</div>
                    ) : null}
                </div>
            ) : null}
        </div>
    );
}
